import { useEffect, useState } from 'react';
import {
  useGetProjectConfigsQuery,
  useListModelPricesQuery,
  useSetProjectConfigsMutation
} from '@/services/api';
import {
  AGENT_MODEL,
  SELECTABLE_MODELS,
  MODEL_LABELS,
  AGENT_ROLES,
  DEFAULT_REASONING_EFFORT,
  SANDBOX_MAPPING,
  defaultRetryMap,
  defaultPauseAfterMap,
  defaultApprovalSlaMap,
  DEFAULT_APPROVAL_SLA_MINUTES,
  DEFAULT_MAX_CONFLICT_RETRIES,
  MAX_CONFLICT_RETRIES_KEY,
  MAX_CONFLICT_RETRIES_MIN,
  MAX_CONFLICT_RETRIES_MAX,
  type ReasoningEffort,
  type RetryPolicy
} from '@/utils/agentConfig';
import { parseApiError } from '@/utils/apiError';
import { useToast } from '@/components/toast/ToastProvider';
import type { AgentRole } from '@/types/api';


function pauseAfterApplies(role: AgentRole): boolean {
  return role !== 'documenter';
}

export function AgentTomlEditor({ projectId, canWrite }: { projectId: string; canWrite: boolean }): JSX.Element {
  const [setConfigs] = useSetProjectConfigsMutation();
  const { data: savedConfigs } = useGetProjectConfigsQuery(projectId);
  const { data: modelPrices } = useListModelPricesQuery();
  const toast = useToast();
  const modelOptions =
    modelPrices?.selectable && modelPrices.selectable.length > 0
      ? modelPrices.selectable
      : SELECTABLE_MODELS.map((m) => ({ model_id: m, label: MODEL_LABELS[m] ?? m }));
  const [active, setActive] = useState<AgentRole>('architect');
  const [reasoning, setReasoning] = useState<Record<AgentRole, ReasoningEffort>>({ ...DEFAULT_REASONING_EFFORT });
  const [model, setModel] = useState<Record<AgentRole, string>>({ ...AGENT_MODEL });
  const [pauseAfter, setPauseAfter] = useState<Record<AgentRole, boolean>>(defaultPauseAfterMap());
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!savedConfigs) return;
    if (dirtyKeys.size > 0) return;
    const nextReasoning = { ...DEFAULT_REASONING_EFFORT };
    const nextPause = defaultPauseAfterMap();
    const nextModel = { ...AGENT_MODEL };
    for (const row of savedConfigs) {
      const mm = /^agent\.([a-z]+)\.model$/.exec(row.key);
      if (mm) {
        const role = mm[1] as AgentRole;
        if (role in nextModel && typeof row.value === 'string' &&
            (SELECTABLE_MODELS as ReadonlyArray<string>).includes(row.value)) {
          nextModel[role] = row.value;
        }
        continue;
      }
      const rm = /^agent\.([a-z]+)\.reasoning_effort$/.exec(row.key);
      if (rm) {
        const role = rm[1] as AgentRole;
        if (role in nextReasoning && (row.value === 'low' || row.value === 'medium' || row.value === 'high')) {
          nextReasoning[role] = row.value;
        }
        continue;
      }
      const pm = /^agent\.([a-z]+)\.pause_after$/.exec(row.key);
      if (pm) {
        const role = pm[1] as AgentRole;
        if (role in nextPause && typeof row.value === 'boolean') {
          nextPause[role] = row.value;
        }
      }
    }
    setReasoning(nextReasoning);
    setPauseAfter(nextPause);
    setModel(nextModel);
  }, [savedConfigs, dirtyKeys]);

  const meta = AGENT_ROLES.find((r) => r.role === active)!;
  const sandbox = SANDBOX_MAPPING[active];

  function markDirty(role: AgentRole, field: string): void {
    setDirtyKeys((s) => new Set(s).add(`${role}.${field}`));
  }

  async function save(): Promise<void> {
    setError(null);
    const agentConfig: Record<
      string,
      { reasoning_effort: ReasoningEffort; pause_after?: boolean; model?: string }
    > = {};
    for (const r of AGENT_ROLES) {
      const reasoningDirty = dirtyKeys.has(`${r.role}.reasoning_effort`);
      const pauseDirty = pauseAfterApplies(r.role) && dirtyKeys.has(`${r.role}.pause_after`);
      const modelDirty = dirtyKeys.has(`${r.role}.model`);
      if (!reasoningDirty && !pauseDirty && !modelDirty) continue;
      agentConfig[r.role] = {
        reasoning_effort: reasoning[r.role],
        ...(pauseDirty ? { pause_after: pauseAfter[r.role] } : {}),
        ...(modelDirty ? { model: model[r.role] } : {})
      };
    }
    if (Object.keys(agentConfig).length === 0) return;
    try {
      await setConfigs({
        projectId,
        body: {
          agent_config: agentConfig,
          retry_policy: {}
        }
      }).unwrap();
      setDirtyKeys(new Set());
      const savedRoles = Object.keys(agentConfig);
      toast.success(
        savedRoles.length === 1
          ? `${AGENT_ROLES.find((r) => r.role === savedRoles[0])?.label ?? 'Agent'} configuration saved.`
          : `${savedRoles.length} agent configurations saved.`
      );
    } catch (err) {
      const message = parseApiError(err).message;
      setError(message);
      toast.error(message);
    }
  }

  return (
    <div className="panel" style={{ maxWidth: 'none', padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)' }}>
        <div className="panel-title" style={{ fontSize: 15 }}>
          Agent TOML configuration
        </div>
        <div className="panel-sub" style={{ margin: '2px 0 0' }}>
          6 canonical agents · structured form
        </div>
      </div>

      <div className="agent-strip">
        {AGENT_ROLES.map((r) => (
          <button
            key={r.role}
            type="button"
            className={`agent-card ${active === r.role ? 'active' : ''}`}
            onClick={() => setActive(r.role)}
            aria-pressed={active === r.role}
          >
            <div className="agent-card-mark" style={{ background: r.colorVar }}>
              {r.code}
            </div>
            <div className="agent-card-name">{r.label}</div>
          </button>
        ))}
      </div>

      <div style={{ padding: '14px 22px' }}>
        <div className="toml-row">
          <div>
            <div className="toml-key mono">role</div>
            <div className="toml-sub">canonical role</div>
          </div>
          <input className="input mono readonly" value={meta.role} readOnly tabIndex={-1} />
          <span className="toml-source">System</span>
        </div>

        <div className="toml-row">
          <div>
            <div className="toml-key mono">model</div>
            <div className="toml-sub">the model this agent runs on, for this project</div>
          </div>
          <select
            className="select mono"
            aria-label="model"
            disabled={!canWrite}
            value={model[active]}
            onChange={(e) => {
              setModel({ ...model, [active]: e.target.value });
              markDirty(active, 'model');
            }}
          >
            {modelOptions.map((m) => (
              <option key={m.model_id} value={m.model_id}>
                {m.label}
              </option>
            ))}
            {!modelOptions.some((m) => m.model_id === model[active]) && model[active] !== '' && (
              <option value={model[active]}>{model[active]} (not currently offered)</option>
            )}
          </select>
          <span className="toml-source proj">Project</span>
        </div>

        <div className="toml-row">
          <div>
            <div className="toml-key mono">reasoning_effort</div>
          </div>
          <select
            className="select mono"
            aria-label="reasoning_effort"
            disabled={!canWrite}
            value={reasoning[active]}
            onChange={(e) => {
              setReasoning({ ...reasoning, [active]: e.target.value as ReasoningEffort });
              markDirty(active, 'reasoning_effort');
            }}
          >
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
          </select>
          <span className="toml-source proj">Project</span>
        </div>

        {pauseAfterApplies(active) && (
          <div className="toml-row">
            <div>
              <div className="toml-key mono">pause_after</div>
              <div className="toml-sub">pause for approval when this role completes successfully</div>
            </div>
            <label className="toml-toggle">
              <button
                type="button"
                role="switch"
                aria-checked={pauseAfter[active]}
                aria-label="pause_after"
                disabled={!canWrite}
                className={`stage-switch ${pauseAfter[active] ? 'on' : ''}`}
                onClick={() => {
                  setPauseAfter({ ...pauseAfter, [active]: !pauseAfter[active] });
                  markDirty(active, 'pause_after');
                }}
              >
                <span className="stage-switch-knob" />
              </button>
              <span>{pauseAfter[active] ? 'on' : 'off'}</span>
            </label>
            <span className="toml-source proj">Project</span>
          </div>
        )}

        <div className="toml-row">
          <div>
            <div className="toml-key mono">sandbox_mode</div>
            <div className="toml-sub">fixed by role · not editable</div>
          </div>
          <input className="input mono readonly" value={sandbox.sandbox_mode} readOnly tabIndex={-1} aria-label="sandbox_mode" />
          <span className="toml-source">System</span>
        </div>
        {sandbox.globs.map((g) => (
          <div className="toml-row" key={g.key}>
            <div>
              <div className="toml-key mono">{g.key}</div>
              <div className="toml-sub">fixed by role · not editable</div>
            </div>
            <input className="input mono readonly" value={g.value} readOnly tabIndex={-1} aria-label={g.key} />
            <span className="toml-source">System</span>
          </div>
        ))}

      </div>

      {error && (
        <div className="field-err" style={{ margin: '0 22px 12px' }} role="alert">
          {error}
        </div>
      )}

      <div className="save-bar">
        {canWrite && (
          <button className="btn primary sm" disabled={dirtyKeys.size === 0} onClick={() => void save()}>
            Save changes ({dirtyKeys.size})
          </button>
        )}
      </div>
    </div>
  );
}


export function RetryEditor({ projectId, canWrite }: { projectId: string; canWrite: boolean }): JSX.Element {
  const [setConfigs] = useSetProjectConfigsMutation();
  const { data: savedConfigs } = useGetProjectConfigsQuery(projectId);
  const toast = useToast();
  const [retry, setRetry] = useState<Record<AgentRole, RetryPolicy>>(defaultRetryMap());
  const [maxConflictRetries, setMaxConflictRetries] = useState<number>(DEFAULT_MAX_CONFLICT_RETRIES);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!savedConfigs) return;
    if (dirtyKeys.size > 0) return;
    const next = defaultRetryMap();
    for (const row of savedConfigs) {
      const m = /^agent\.([a-z]+)\.retry_policy$/.exec(row.key);
      if (!m) continue;
      const role = m[1] as AgentRole;
      if (!(role in next)) continue;
      const v = row.value as Partial<RetryPolicy> | null;
      if (v && typeof v === 'object') {
        if (typeof v.max_attempts === 'number') next[role].max_attempts = v.max_attempts;
        if (typeof v.timeout_seconds === 'number') next[role].timeout_seconds = v.timeout_seconds;
      }
    }
    setRetry(next);
    const capRow = savedConfigs.find((row) => row.key === MAX_CONFLICT_RETRIES_KEY);
    setMaxConflictRetries(
      capRow && typeof capRow.value === 'number' ? capRow.value : DEFAULT_MAX_CONFLICT_RETRIES
    );
  }, [savedConfigs, dirtyKeys]);

  function markDirty(key: string): void {
    setDirtyKeys((s) => new Set(s).add(key));
  }

  function update(role: AgentRole, field: keyof RetryPolicy, raw: string): void {
    const value = Number(raw);
    if (raw !== '' && (!Number.isInteger(value) || value < 1)) {
      setValidationError(`${role} ${field} must be a positive whole number.`);
    } else {
      setValidationError(null);
    }
    setRetry({ ...retry, [role]: { ...retry[role], [field]: value } });
    markDirty(`${role}.${field}`);
  }

  const dirtyCount = dirtyKeys.size;

  function updateCap(raw: string): void {
    const value = Number(raw);
    if (raw !== '' && (!Number.isInteger(value) || value < MAX_CONFLICT_RETRIES_MIN || value > MAX_CONFLICT_RETRIES_MAX)) {
      setValidationError(`Rework loop retries must be a whole number between ${MAX_CONFLICT_RETRIES_MIN} and ${MAX_CONFLICT_RETRIES_MAX}.`);
    } else {
      setValidationError(null);
    }
    setMaxConflictRetries(value);
    markDirty(MAX_CONFLICT_RETRIES_KEY);
  }

  async function save(): Promise<void> {
    const retryPolicy: Record<string, RetryPolicy> = {};
    for (const r of AGENT_ROLES) {
      if (dirtyKeys.has(`${r.role}.max_attempts`) || dirtyKeys.has(`${r.role}.timeout_seconds`)) {
        retryPolicy[r.role] = retry[r.role];
      }
    }
    const capDirty = dirtyKeys.has(MAX_CONFLICT_RETRIES_KEY);
    if (Object.keys(retryPolicy).length === 0 && !capDirty) return;
    try {
      await setConfigs({
        projectId,
        body: {
          agent_config: {},
          retry_policy: retryPolicy,
          ...(capDirty ? { max_conflict_retries: maxConflictRetries } : {})
        }
      }).unwrap();
      setDirtyKeys(new Set());
      toast.success('Retry & timeout policy saved.');
    } catch (err) {
      toast.error(parseApiError(err).message);
    }
  }

  return (
    <>
      <div className="page-header">
        <h2 className="panel-title">Retry &amp; timeout</h2>
      </div>

      <div className="panel" style={{ maxWidth: 'none', padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)' }}>
          <div className="panel-title" style={{ fontSize: 15 }}>
            Retry &amp; timeout policy
          </div>
          <div className="panel-sub" style={{ margin: '2px 0 0' }}>
            Backoff strategy uses recommended defaults.
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Role</th>
              <th>Mode</th>
              <th>Retry attempts</th>
              <th>Timeout (s)</th>
            </tr>
          </thead>
          <tbody>
            {AGENT_ROLES.map((r) => (
              <tr key={r.role}>
                <td>{r.label}</td>
                <td className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {SANDBOX_MAPPING[r.role].sandbox_mode}
                </td>
                <td>
                  <input
                    className="input mono cell"
                    type="number"
                    aria-label={`${r.label} max_attempts`}
                    disabled={!canWrite}
                    value={retry[r.role].max_attempts}
                    onChange={(e) => update(r.role, 'max_attempts', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="input mono cell"
                    type="number"
                    aria-label={`${r.label} timeout_seconds`}
                    disabled={!canWrite}
                    value={retry[r.role].timeout_seconds}
                    onChange={(e) => update(r.role, 'timeout_seconds', e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: '16px 22px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div className="panel-title" style={{ fontSize: 13.5, margin: 0 }}>Rework loop retries</div>
            <div className="panel-sub" style={{ margin: '2px 0 0' }}>
              Rework attempts before the run escalates for human review (default 3).
            </div>
          </div>
          <input
            className="input mono cell"
            type="number"
            min={MAX_CONFLICT_RETRIES_MIN}
            max={MAX_CONFLICT_RETRIES_MAX}
            aria-label="Rework loop retries"
            disabled={!canWrite}
            value={maxConflictRetries}
            onChange={(e) => updateCap(e.target.value)}
          />
        </div>
        {validationError && (
          <div className="field-err" style={{ margin: '0 22px 12px' }} role="alert" data-testid="retry-validation-error">
            {validationError}
          </div>
        )}
        <div className="save-bar">
            {canWrite && (
            <button
              className="btn primary sm"
              disabled={dirtyCount === 0 || validationError !== null}
              onClick={() => void save()}
            >
              Save changes ({dirtyCount})
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export function SlaEditor({ projectId, canWrite }: { projectId: string; canWrite: boolean }): JSX.Element {
  const [setConfigs] = useSetProjectConfigsMutation();
  const { data: savedConfigs } = useGetProjectConfigsQuery(projectId);
  const toast = useToast();
  const [pauseAfter, setPauseAfter] = useState<Record<AgentRole, boolean>>(defaultPauseAfterMap());
  const [sla, setSla] = useState<Record<AgentRole, number>>(defaultApprovalSlaMap());
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!savedConfigs) return;
    if (dirtyKeys.size > 0) return;
    const nextPause = defaultPauseAfterMap();
    const nextSla = defaultApprovalSlaMap();
    for (const row of savedConfigs) {
      const pm = /^agent\.([a-z]+)\.pause_after$/.exec(row.key);
      if (pm) {
        const role = pm[1] as AgentRole;
        if (role in nextPause && typeof row.value === 'boolean') nextPause[role] = row.value;
        continue;
      }
      const sm = /^agent\.([a-z]+)\.approval_sla_minutes$/.exec(row.key);
      if (sm) {
        const role = sm[1] as AgentRole;
        if (role in nextSla && typeof row.value === 'number') nextSla[role] = row.value;
      }
    }
    setPauseAfter(nextPause);
    setSla(nextSla);
  }, [savedConfigs, dirtyKeys]);

  const rows = AGENT_ROLES.filter((r) => r.role !== 'documenter');
  const dirtyCount = dirtyKeys.size;

  function update(role: AgentRole, raw: string): void {
    const value = Math.max(1, Math.floor(Number(raw) || 0));
    if (raw !== '' && (!Number.isInteger(Number(raw)) || Number(raw) < 1)) {
      setValidationError(`${role} SLA must be a positive whole number of minutes.`);
    } else {
      setValidationError(null);
    }
    setSla({ ...sla, [role]: value });
    setDirtyKeys((s) => new Set(s).add(`${role}.approval_sla_minutes`));
  }

  async function save(): Promise<void> {
    const agentConfig: Record<string, { reasoning_effort: string; approval_sla_minutes?: number }> = {};
    for (const r of rows) {
      if (!pauseAfter[r.role]) continue;
      if (!dirtyKeys.has(`${r.role}.approval_sla_minutes`)) continue;
      agentConfig[r.role] = { reasoning_effort: DEFAULT_REASONING_EFFORT[r.role], approval_sla_minutes: sla[r.role] };
    }
    if (Object.keys(agentConfig).length === 0) return;
    try {
      await setConfigs({ projectId, body: { agent_config: agentConfig, retry_policy: {} } }).unwrap();
      setDirtyKeys(new Set());
      toast.success('Approval SLA saved.');
    } catch (err) {
      const message = parseApiError(err).message;
      setValidationError(message);
      toast.error(message);
    }
  }

  return (
    <div className="panel">
      <h2 className="panel-title">Set SLA</h2>
      <p className="panel-sub" style={{ margin: '2px 0 16px' }}>
        Turnaround target for each paused approval gate. On breach, the platform notifies this
        project&apos;s approvers and Admin — it never auto-decides.
      </p>
      {!canWrite && (
        <div className="banner" role="status" style={{ marginBottom: 16 }}>
          <span className="banner-icon">i</span>
          <span>You have read-only access to this project&apos;s settings.</span>
        </div>
      )}
      {rows.map((r) => {
        const on = pauseAfter[r.role];
        return (
          <div
            key={r.role}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '12px 0',
              borderBottom: '1px solid var(--border)'
            }}
          >
            <span className="agent-mark" style={{ background: r.colorVar, flexShrink: 0 }}>{r.code}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="agent-name">{r.label}</div>
              <div className="agent-sub">{ROLE_STAGE_ID[r.role]}{on ? '' : ' · read-only'}</div>
            </div>
            <span className="agent-sub" style={{ width: 64, textAlign: 'right', flexShrink: 0 }}>
              gate: {on ? <strong style={{ color: 'var(--amber, #b45309)' }}>on</strong> : 'off'}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, width: 150, justifyContent: 'flex-end' }}>
              <input
                type="number"
                min={1}
                step={1}
                className="input"
                style={{ width: 84 }}
                aria-label={`${r.label} approval SLA minutes`}
                disabled={!on || !canWrite}
                value={on ? sla[r.role] : DEFAULT_APPROVAL_SLA_MINUTES}
                onChange={(e) => update(r.role, e.target.value)}
              />
              <span className="agent-sub" style={{ width: 52 }}>minutes</span>
            </div>
          </div>
        );
      })}
      {validationError && (
        <div className="banner error" role="alert" style={{ marginTop: 12 }}>
          <span className="banner-icon">!</span>
          <span>{validationError}</span>
        </div>
      )}
      {canWrite && (
        <div style={{ display: 'flex', gap: 10, marginTop: 20, paddingTop: 18, borderTop: '1px solid var(--border)' }}>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={dirtyCount === 0}>
            Save changes ({dirtyCount})
          </button>
        </div>
      )}
    </div>
  );
}

const ROLE_STAGE_ID: Record<string, string> = {
  explorer: 'explore',
  architect: 'plan',
  coder: 'code',
  reviewer: 'review',
  tester: 'test',
  documenter: 'document'
};
